// Identity, and the two things somebody might do with it.
//
// The address and a Sign out button used to sit in the masthead beside Refresh, which put a raw
// email in the loudest part of a page that gets shared on a screen in meetings. Both live behind
// the initials now.
//
// The admin rebuild arrives here too, and it is the whole reason the Refresh button could go:
// for everybody else that button re-fetched the same cached payload, so it did nothing a page
// reload does not. Forcing an actual rebuild spends about eleven seconds of Snowflake on
// everybody's behalf, which is a deliberate act rather than a button you press while reading.

import { Link } from 'react-router-dom';
import { Popover } from './Popover';

// Two letters from the address, because Clerk hands us an email and not a name. `eva.zumwalt@`
// gives EZ; a single-word local part gives its first two letters.
function initialsFor(email: string): string {
  const localPart = email.split('@')[0] ?? '';
  const words = localPart.split(/[._-]+/).filter(Boolean);
  const firstWord = words[0] ?? '';
  const secondWord = words[1] ?? '';
  if (firstWord && secondWord) {
    return (firstWord.slice(0, 1) + secondWord.slice(0, 1)).toUpperCase();
  }
  return localPart.slice(0, 2).toUpperCase() || '?';
}

export function ProfileMenu({
  email,
  role,
  canRebuild,
  isRebuilding,
  onRebuild,
  onSignOut,
  showsStyle = false,
}: {
  // Null in the `DASHBOARD_AUTH_DISABLED` local mode, where there is nobody to name.
  email: string | null;
  role: string | null;
  canRebuild: boolean;
  isRebuilding: boolean;
  // Absent on the activity board, which has no rebuild endpoint behind it.
  onRebuild?: () => void;
  onSignOut: () => void;
  // Settings → My style, offered with the Scout tab (W7).
  showsStyle?: boolean;
}) {
  const label = email ?? 'Local development';

  return (
    <Popover
      label={`Account: ${label}`}
      triggerClass="avatar"
      trigger={email ? initialsFor(email) : 'DEV'}
      panelClass="profilepop"
    >
      <p className="popwho">{label}</p>
      {email
        ? role ? <p className="poprole">{role}</p> : null
        : <p className="poprole">Auth disabled</p>}

      <hr />

      {showsStyle ? (
        <Link className="popact" to="/settings/style">
          My email style
          <span className="hint">How Scout writes your drafts</span>
        </Link>
      ) : null}

      {/* Admin only, and it says what it costs -- eleven seconds is long enough that somebody
          who does not know will assume the page has hung. */}
      {canRebuild && onRebuild ? (
        <button type="button" className="popact" disabled={isRebuilding} onClick={onRebuild}>
          {isRebuilding ? 'Rebuilding…' : 'Rebuild data now'}
          <span className="hint">
            {isRebuilding
              ? 'Re-reading Snowflake, about eleven seconds'
              : 'Re-reads Snowflake for everyone · about 11s'}
          </span>
        </button>
      ) : null}

      {/* Nothing to sign out of when auth is off, and offering it would reload the page for no
          reason. */}
      {email ? (
        <button type="button" className="popact" onClick={onSignOut}>
          Sign out
        </button>
      ) : null}
    </Popover>
  );
}
